import { h, Component } from 'preact';
import { widgetConfiguration } from '@sgwt-widget/core';
import {
  IAccountCenterLink,
  IAuthenticationModeData,
  IUserDropdownLink,
  ISgwtSuperHeaderI18NLanguage,
  ISuperHeaderProps,
  ISuperHeaderState,
  IMyServicesLink,
  IAuthenticationInformation
} from '../sgwt-super-header.types';

import MyServicesLink from './MyServicesLink';
import NavigateAs from './NavigateAs';
import UserDropdown from './UserDropdown';
import UserDropdownMenuXs from './UserDropdownMenuXs';
import LanguageSelector from './LanguageSelector';
import SignOutLink from './SignOutLink';
import SuperHeaderTags from './SuperHeaderTags';
import { SGWT_SUPER_HEADER_I18N } from '../sgwt-super-header.i18n';
import {
  getInternationalizationLabels,
  isEmbeddedInIOS,
  isEmbeddedInAndroid,
  getClaimsFromSgwtConnect
} from '../../common/sgwt-widgets-utils';
import { BUS_ACCESS_TOKEN, BUS_GLOBAL_LANGUAGE, BUS_USER_CONNECTION, ISgwtUserConnectionStatus } from '../../common/bus-topics';
import { SubscriptionHandle } from '@sgwt-widget/core/lib/bus/Bus';

// const hostRegex = RegExp('^.*(.sgmarkets.com)$', 'gi');

export default class SgwtSuperHeader extends Component<ISuperHeaderProps, ISuperHeaderState> {
  private authentication: IAuthenticationModeData | null;
  private defaultMyServicesLink: string;
  private defaultAccountCenterLink: string;
  private accessTokenSubscription?: string;
  private userConnectionSubscription?: SubscriptionHandle;
  private sgConnectV2ManagedByBus = false;
  private embeddedInWebView = false;

  constructor(props: ISuperHeaderProps) {
    super(props);
    const currentUrl = document.location.href;
    const accountCenterLinks: IAccountCenterLink = {
      homologation: 'https://sgr-web-serviceboard-uat.fr.world.socgen/profilecenter',
      production: 'https://www.sgmarkets.com/profilecenter'
    };
    const myServicesLinks: IMyServicesLink = {
      homologation: 'https://sgr-web-serviceboard-uat.fr.world.socgen/services',
      production: 'https://www.sgmarkets.com/services'
    };
    const authenticationsInfo: IAuthenticationInformation = {
      'sg-connect-v1': {
        homologation: {
          authenticationEndpoint: 'https://sgr-web-serviceboard-uat.fr.world.socgen/api/v1/me',
          changePasswordLink: 'https://sgconnect-hom.fr.world.socgen/#/change-password',
          signOutLink: 'https://sgconnect-hom.fr.world.socgen/#/logout'
        },
        production: {
          authenticationEndpoint: 'https://www.sgmarkets.com/api/v2/me',
          changePasswordLink: 'https://sso.sgmarkets.com/#/change-password',
          signOutLink: 'https://sso.sgmarkets.com/#/logout'
        }
      },
      'sg-connect-v2': {
        homologation: {
          authenticationEndpoint: null,
          changePasswordLink: 'https://sgconnect-hom.fr.world.socgen/#/change-password',
          signOutLink: 'https://sgconnect-hom.fr.world.socgen/#/logout'
        },
        production: {
          authenticationEndpoint: null,
          changePasswordLink: 'https://sso.sgmarkets.com/#/change-password',
          signOutLink: 'https://sso.sgmarkets.com/#/logout'
        }
      }
    };
    const envFromConfig = widgetConfiguration().environment.toLowerCase();
    const env = (envFromConfig === 'production' || envFromConfig === 'prod') ? 'production' : 'homologation';
    this.authentication = props.sgConnectSupport ? authenticationsInfo[props.sgConnectSupport][env] : null;
    this.defaultMyServicesLink = myServicesLinks[env] || myServicesLinks.production;
    this.defaultAccountCenterLink = accountCenterLinks[env] || accountCenterLinks.production;
    this.embeddedInWebView = isEmbeddedInIOS() || isEmbeddedInAndroid();
  }

  private logDebug(...messages: any[]) {
    if (this.props.debug) {
      widgetConfiguration().log('[sgwt-super-header::debug]', ...messages);
    }
  }

  private checkStatus(response: Response): Response {
    const [codeOK, codeKO] = [200, 300];
    if (response.status >= codeOK && response.status < codeKO) {
      return response;
    } else {
      throw new Error(response.statusText);
    }
  }

  /**
   * A user has been detected (with SSO v1 authentication or SG Connect v2 without the usage of Widget bus).
   * We put some information in the Widgets bus, and call the
   */
  private onUserConnection(mail: string, claims?: any) {
    this.props.onConnection({mail});
    // issue #25 MiniFooter - Force support for SSOv1 or SSOv2 no bus
    widgetConfiguration().bus.publish<ISgwtUserConnectionStatus>(BUS_USER_CONNECTION, {
      connected: true,
      mail,
      claims
    });
  }

  /**
   * Identify the user with SSO v1.
   **/
  private authenticateWithSgConnectV1(endpoint: string) {
    this.logDebug('authenticate with SSO v1');
    widgetConfiguration()
      .fetch(endpoint, {
        headers: new Headers({
          accept: 'application/json'
        }),
        credentials: 'include'
      })
      .then(this.checkStatus)
      .then(response => response.json())
      .then((data: any) => {
        if (data.Username) { // sg-connect-v1
          this.onUserConnection(data.Username);
        } else {
          this.logDebug('no required information found with SSO v1 authentication', data);
        }
      })
      .catch((err: Error): void => {
        widgetConfiguration().log(`Failed to authenticate the user with SG Connect v1: ${err}.`);
      });
  }

  /**
   * For SG Connect v2 mode and in case the Widget bus is not used, we try to find the sgwtConnect instance in `window`.
   */
  private fallbackForSGConnectV2AuthenticationWithoutBus() {
    this.logDebug('fallback for SG Connect v2 support, without bus');
    if (this.sgConnectV2ManagedByBus) { // Bus was used in fact, maybe a concurrency race issue?
      return;
    }
    const sgwtConnect = (window as any).sgwtConnect;
    // Found it!
    if (sgwtConnect) {
      const user = getClaimsFromSgwtConnect(sgwtConnect);
      this.logDebug('sg-connect-v2-fallback, got user', user);
      if (user && user.sub) {
        this.onUserConnection(user.sub, user);
        this.logDebug('publish user information on widgets bus');
      } else {
        // We are in a specific case: the sgwtConnect instance has been found, but the user does not exists - meaning
        // that sgwtConnect.isAuthorized() is false. We will wait for the event of token renewal...
        // This is useful for applications that make a silent authorization request (`renewAuthorization()`) in fact.
        sgwtConnect.on('renewAuthorizationSuccess', () => {
          const user = getClaimsFromSgwtConnect(sgwtConnect);
          this.logDebug('sg-connect-v2-fallback, got user after a Token Renewal event', user);
          if (user && user.sub) {
            this.onUserConnection(user.sub, user);
            this.logDebug('Publish user information on widgets bus');
          }
        });
      }
    }
  }

  /**
   * Identify the user with SG Connect v2.
   * This is called when we register to the corresponding topic on Widget bus.
   * In case the bus is not used, we call the `fallbackForSGConnectV2AuthenticationWithoutBus`.
   **/
  private manageSGConnectV2Connection(status?: any) {
    this.sgConnectV2ManagedByBus = status !== undefined; // We know that the bus is used
    if (status && status.connected) {
      // user is connected.
      if (!this.props.user) {
        // Case 1: user just connected => emit sign-in event.
        if (!status.mail) {
          status.mail = status.claims.sub;
        }
        setTimeout(() => {
          this.props.onConnection(status);
        });
      }
    } else {
      // User is not connected
      if (this.props.user) {
        // Case 2: user was connected. So it's a disconnection.
        this.props.onSignOut();
      } else {
        // Case 3: the <sgwt-connect> is not used, so we should use the fallback...
        setTimeout(() => {
          this.fallbackForSGConnectV2AuthenticationWithoutBus();
        }, 1000);
      }
    }
  }

  public componentWillMount() {
    if (this.props.sgConnectSupport === 'sg-connect-v1' && this.authentication && this.authentication.authenticationEndpoint) {
      this.authenticateWithSgConnectV1(this.authentication.authenticationEndpoint);
    } else if (this.props.sgConnectSupport === 'sg-connect-v2') {
      // Listen to user connection status, from the Widgets bus
      if (customElements.get('sgwt-connect') !== undefined) {
        this.logDebug('sg-connect-v2 support | subscribe to widgets bus for connection status');
        this.accessTokenSubscription = widgetConfiguration().bus.subscribe<string>(
          BUS_ACCESS_TOKEN,
          (token?: string) => {
            this.setState({ token });
        });
        this.userConnectionSubscription = widgetConfiguration().bus
          .subscribe<ISgwtUserConnectionStatus>(
            BUS_USER_CONNECTION,
            status => {
              this.manageSGConnectV2Connection(status);
            }
          );
      } else {
        this.logDebug('sg-connect-v2 support | manage connection without <sgwt-connect>');
        this.manageSGConnectV2Connection();
      }
    }
    // Get current language and publish it in the Widget Bus
    const currentLanguage = this.props.language || 'en';
    this.logDebug('publish language on bus');
    widgetConfiguration().bus.publish(BUS_GLOBAL_LANGUAGE, currentLanguage);
  }

  public componentWillUnmount() {
    if (this.accessTokenSubscription) {
      widgetConfiguration().bus.unsubscribe(this.accessTokenSubscription);
    }
    if (this.userConnectionSubscription) {
      widgetConfiguration().bus.unsubscribe(this.userConnectionSubscription);
    }
  }

  signOut() {
    this.props.onSignOut();
  }

  changeLanguage(language: string) {
    this.logDebug(`language changed to ${language}, publish to bus`);
    widgetConfiguration().bus.publish(BUS_GLOBAL_LANGUAGE, language);
    this.props.onLanguageChanged(language);
  }

  render(props: ISuperHeaderProps, state: ISuperHeaderState): JSX.Element {
    if (this.embeddedInWebView) {
      this.logDebug('The Super Header is embedded in WebView, so not visible.');
      return <span/>;
    }
    const currentLanguage = props.language || 'en';
    const labels: ISgwtSuperHeaderI18NLanguage =
      getInternationalizationLabels(SGWT_SUPER_HEADER_I18N, props.additionalI18n, currentLanguage);

    let signOutLink = props.signOutLink;
    if (!signOutLink && this.authentication && this.authentication.signOutLink) {
      signOutLink = this.authentication.signOutLink;
    }

    let containerClass = props.container === 'container-fluid' ? 'container-fluid' : 'container';

    const isSGMarkets =
      (this.props.sgConnectSupport && this.props.sgConnectSupport.toLowerCase() === 'sg-connect-v1') ||
      (this.props.mode && this.props.mode.toLowerCase() === 'sg-markets') ||
      (window as any).location.host.toLowerCase().indexOf('.sgmarkets.com') > -1;

    return (
      <nav class="topbar topbar-dark sgwt-super-header" role="navigation">
        <div class={ containerClass }>
          {/* My Services link */}
          <ul class="nav topbar-nav">
            <MyServicesLink
              labels={labels}
              hideServicesLink={ props.hideServicesLink }
              isSGMarkets={isSGMarkets}
              defaultLink={ this.defaultMyServicesLink }
              customLink={ props.myServicesLink }
              customLabel={ props.myServicesLabel }
              customIcon={ props.myServicesIcon }
            />
          </ul>

          <ul class="nav topbar-nav topbar-right">
            {/* Navigate As */}
            <NavigateAs
              labels={ labels }
              user={ props.user }
              navigateAs={ props.navigateAs.active }
              navigateAsList={ props.navigateAs.list }
              onNavigateAsSelectUser={ (user: any) => props.navigateAs.onSelectUser(user) }
              onNavigateAsClick={ () => props.navigateAs.onMenuClick() }
            />

            {/* User dropdown */}
            <UserDropdown
              labels={labels}
              user={props.user}
              authentication={this.authentication}
              defaultAccountCenter={isSGMarkets ? this.defaultAccountCenterLink : ''}
              userDropdownLinks={props.userDropdownLinks}
              onUserDropdownLinkClicked={(link: IUserDropdownLink) => props.onUserDropdownLinkClicked(link)}
            />
            {/* User dropdown on XS devices */}
            <UserDropdownMenuXs
              labels={labels}
              user={props.user}
              authentication={this.authentication}
              defaultAccountCenter={isSGMarkets ? this.defaultAccountCenterLink : ''}
              userDropdownLinks={props.userDropdownLinks}
              onUserDropdownLinkClicked={(link: IUserDropdownLink) => props.onUserDropdownLinkClicked(link)}

              language={ currentLanguage }
              availableLanguages={ props.availableLanguages }
              onLanguageChanged={ lang => this.changeLanguage(lang) }
              hideCurrentLanguage={ props.hideCurrentLanguage }

              signOutLink={signOutLink}
              onSignOut={() => this.signOut()}
            />

            {/* Language sub-menu */}
            <LanguageSelector
              language={ currentLanguage }
              availableLanguages={ props.availableLanguages }
              onLanguageChanged={ lang => this.changeLanguage(lang) }
              hideCurrentLanguage={ props.hideCurrentLanguage }
            />

            {/* Sign-out */}
            <SignOutLink
              labels={labels}
              user={props.user}
              signOutLink={signOutLink}
              onSignOut={() => this.signOut()}
              sgConnectSupport={props.sgConnectSupport}
            />
          </ul>
        </div>
        <SuperHeaderTags
          labels={labels}
          environment={ props.environment }
          navigateAs={ props.navigateAs.asUser }
          stopNavigationAs={ () => props.navigateAs.onStopNavigationAs() }
        />
      </nav>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/sgwt-super-header/components/SuperHeader.tsx